import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  BackdropForDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OnboardingStatusDots,
  SubmitButton,
} from 'components';
import { EOnboardingStep } from 'enums';
import { ZoneForm } from 'forms';
import { IZone } from 'models';
import { useOnboardingContext } from './onboarding.context.hook';
import { DialogPrevNextClose } from './dialog-prev-next-close';

interface OnboardingZoneDialogProps {
  visible?: boolean;
}

export const OnboardingZoneDialog = ({ visible = false }: OnboardingZoneDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading_form, setLoadingForm] = useState<boolean>(false);
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);
  const { setVisible, buildingId, zoneId, updateOnboarding } = useOnboardingContext();

  const formRef = React.useRef<any>();

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  const onNext = zoneId
    ? () => {
        updateOnboarding({ status: EOnboardingStep.COMPANY_BRAND_SETTING });
      }
    : onSubmit;

  const onPrev = () => {
    updateOnboarding({ status: EOnboardingStep.SETUP_BUILDING });
  };

  const next = (zone?: IZone) => {
    const zoneId = zone?.id;

    if (zoneId) {
      updateOnboarding({
        zoneId,
        status: EOnboardingStep.COMPANY_BRAND_SETTING,
      });
    }
  };

  const title = 'Let’s create your zone.';
  const subtitle = 'Zones are high-touch spaces you’d like to track.';
  const submitButtonLabel = down_sm ? 'Next' : 'Set Up Company Next';

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <DialogPrevNextClose onPrev={onPrev} onNext={onNext} onClose={onClose} />

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Step 2 of 6
          </Typography>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h5">{title}</Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              marginTop: 1,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box paddingTop={2}>
          <ZoneForm
            ref={formRef}
            initialBuildingId={buildingId}
            zoneId={zoneId}
            onInit={(loading) => setLoadingForm(loading)}
            onSubmitting={(loading) => setLoadingSubmit(loading)}
            next={next}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <SubmitButton loading={loading_submit} label={submitButtonLabel} onClick={onSubmit} />
      </DialogActions>

      <Box
        sx={{
          width: '100%',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
        }}
      >
        <OnboardingStatusDots status={EOnboardingStep.SETUP_ZONE} />
      </Box>

      <BackdropForDialog loading={loading_form} />
    </Dialog>
  );
};
